import { removeEmptyFields } from "@/lib/utils";
import { useCreateLabSetupMutation } from "@/services/school/lab/useAddLabSetupDetails";
import { useGetLabSetupBySchoolIdQuery } from "@/services/school/lab/useGetLabSetupById";
import { useUpdateLabSetupMutation } from "@/services/school/lab/useUpdateLabSetupDetails";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { FormValues, schema } from "./validations";

export const useAddEditLabSetup = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  // Fetch existing Lab Setup data by schoolId
  const { data: existingData, isLoading: isLoadingExisting } =
    useGetLabSetupBySchoolIdQuery(id || "", { enabled: !!id });

  const isEditMode = !!existingData?.data?.labSetupDetails;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      targetDate: "",
      noOfWorkstations: "",
      labSetupStatus: "To-Do",
      specialRequirements: "",
      comment: "",
    },
  });

  // No region/relationship profile lookups required for lab setup form

  // Lab setup create/update mutations
  // Lab setup create/update mutations
const { mutate: createLabSetup, isPending: isCreating } =
  useCreateLabSetupMutation({
    onSuccess: () => {
      form.reset();
      toast.success("Lab Setup added successfully");
    },
  });

const { mutate: updateLabSetup, isPending: isUpdating } =
  useUpdateLabSetupMutation({
    onSuccess: () => {
      toast.success("Lab Setup updated successfully");
    },
  });


  const isSubmitting = isCreating || isUpdating;


  // Populate form with existing data when in edit mode
  useEffect(() => {
    if (existingData?.data && isEditMode) {
      const existing = existingData.data.labSetupDetails;
      // API returns only labSetupDetails for this endpoint; map those to the form
      const validStatuses = [
        "To-Do",
        "On Track",
        "Delayed",
        "Lab Done",
        "Stocking Done",
      ] as const;
      const labSetupStatus =
        existing?.labSetupStatus &&
        validStatuses.includes(existing.labSetupStatus as any)
          ? (existing.labSetupStatus as (typeof validStatuses)[number])
          : "To-Do";

      form.reset({
        targetDate: existing?.targetDate
          ? existing.targetDate.split("T")[0]
          : "",
        noOfWorkstations:
          existing?.noOfWorkstations !== undefined &&
          existing?.noOfWorkstations !== null
            ? String(existing.noOfWorkstations)
            : "",
        specialRequirements: existing?.specialRequirements ?? "",
        labSetupStatus,
        comment: existing?.comment ?? "",
      });
    }
    setIsLoading(false);
  }, [existingData, isEditMode, form.reset]);

  const onSubmit = (values: FormValues) => {
    if (!id) return;

    // Map form values to API payload shapes
    if (isEditMode) {
      const updateBody = {
        schoolId: id,
        targetDate: values.targetDate,
        noOfWorkstations: Number(values.noOfWorkstations),
        specialRequirements: values.specialRequirements || "",
        labSetupStatus: values.labSetupStatus,
        comment: values.comment || "",
      };
      updateLabSetup(updateBody as any);
    } else {
      const createBody = {
        schoolId: id,
        targetDate: values.targetDate,
        noOfWorkstations: Number(values.noOfWorkstations),
        specialRequirements: values.specialRequirements || "",
        labSetupStatus: values.labSetupStatus,
        comment: values.comment || "",
      };
      createLabSetup(createBody as any);
    }
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    regionHeadOptions: [],
    relationshipManagerOptions: [],
    isLoadingRegionHeads: false,
    isLoadingRelationshipManagers: false,
    selectedRegionHead: undefined,
    selectedRelationshipManager: undefined,
    isEditMode,
    isLoadingExisting: isLoadingExisting || isLoading,
  };
};
